import cx from 'classnames';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import React, { ReactNode, useEffect, useRef } from 'react';
import styles from '../layout.module.scss';

export enum ContentWidth {
  FLUID = 'fluidWidth',
  FIXED = 'fixedWidth',
}

export interface ContentProps {
  /** Child Element to be render as part of Main / Center UI */
  children: ReactNode;

  /**
   * Width of the Content.
   * `ContentWidth.FLUID` takes 100% width.
   * `ContentWidth.FIXED` set the content max-width as 1280px.
   * @default 'ContentWidth.FLUID'
   */
  contentWidth?: ContentWidth;

  /**
   * noPadding to override any padding.
   * @default false
   */
  noPadding?: boolean;

  /**
   * Use browser default scroll instead of overlay scroll
   * @default false
   */
  disableOverlayScroll?: boolean;

  /**
   * When childern of content changes the page is  scrolled to top
   * @default false
   */
  shouldScrollOnChildrenChange?: boolean;

  /**
   * A custom className to be used to extend/override the styles,
   * This is required for the left navigation hub to override display property for the main route content.
   */
  className?: string;
}

export const Content = ({
  children,
  contentWidth = ContentWidth.FLUID,
  noPadding = false,
  disableOverlayScroll = false,
  shouldScrollOnChildrenChange = false,
  className = '',
}: ContentProps) => {
  const contentDivRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (shouldScrollOnChildrenChange) contentDivRef.current?.scrollTo(0, 0);
  }, [children, shouldScrollOnChildrenChange]);

  const innerContainerClass = cx(styles.innerContainer, {
    [styles.fixedWidth]: contentWidth === ContentWidth.FIXED,
    [styles.noPadding]: noPadding,
    [className]: className !== '',
  });
  const pageContent = (
    <main className={styles.page}>
      {/* innerContainer should be it's own container component */}
      <div className={innerContainerClass}>
        {/* {main page children} */}
        {children}
      </div>
    </main>
  );

  return disableOverlayScroll ? (
    <div data-testid="content" className={styles.contentNonOverlayScroll} ref={contentDivRef}>
      {pageContent}
    </div>
  ) : (
    <OverlayScrollbarsComponent
      options={{
        scrollbars: { autoHide: 'move' },
      }}
      className={cx('os-host-flexbox', styles.contentOverlayScroll)}
    >
      {pageContent}
    </OverlayScrollbarsComponent>
  );
};
